import type {
  PublicScanConfirmResponse,
  PublicScanReportResponse,
  PublicScanRequestRequest,
  PublicScanRequestResponse,
  PublicScanStatusResponse,
} from '@brand24-original/llm-types';
import { client } from '../../../shared/services/apiClient';

const SCAN_ENDPOINT = '/scan';

/**
 * Hasło tymczasowej bramki (`PUBLIC_SCAN_ACCESS_PASSWORD` po stronie backendu). Trzymamy je w
 * `sessionStorage`, więc przetrwa odświeżenie strony, ale zamknięcie karty kasuje dostęp.
 */
const PASSWORD_KEY = 'chatbeat.scan-access';
const ACCESS_HEADER = 'x-access-password';

export const readScanPassword = (): string => sessionStorage.getItem(PASSWORD_KEY) ?? '';
export const storeScanPassword = (password: string): void =>
  sessionStorage.setItem(PASSWORD_KEY, password);

// `client(true, false)` = bez prefiksu `/api` i bez tokena — skan jest publiczny, tak jak trasy auth.
// Hasło bramki dokładamy nagłówkiem: przeglądarka nie pokaże okna Basic dla zapytań z JS.
const publicClient = () => {
  const instance = client(true, false);
  const password = readScanPassword();
  if (password) {
    instance.defaults.headers.common[ACCESS_HEADER] = password;
  }
  return instance;
};

/**
 * Sprawdza hasło bramki. Trasa `/scan/access` sama nic nie robi — o wyniku decyduje middleware przed
 * routerem, więc 204 oznacza „hasło poprawne" (albo „bramka wyłączona"), a 401 „błędne".
 */
export const checkPublicScanAccess = async (password: string): Promise<boolean> => {
  try {
    await client(true, false).get(`${SCAN_ENDPOINT}/access`, {
      headers: password ? { [ACCESS_HEADER]: password } : undefined,
    });
    return true;
  } catch {
    return false;
  }
};

export const requestPublicScan = async (
  data: PublicScanRequestRequest
): Promise<PublicScanRequestResponse> => {
  const response = await publicClient().post<PublicScanRequestResponse>(
    `${SCAN_ENDPOINT}/request`,
    data
  );
  return response.data;
};

export const confirmPublicScan = async (
  confirmToken: string
): Promise<PublicScanConfirmResponse> => {
  const response = await publicClient().post<PublicScanConfirmResponse>(
    `${SCAN_ENDPOINT}/${confirmToken}/confirm`
  );
  return response.data;
};

export const getPublicScanStatus = async (
  reportToken: string
): Promise<PublicScanStatusResponse> => {
  const response = await publicClient().get<PublicScanStatusResponse>(
    `${SCAN_ENDPOINT}/${reportToken}/status`
  );
  return response.data;
};

export const getPublicScanReport = async (
  reportToken: string
): Promise<PublicScanReportResponse> => {
  const response = await publicClient().get<PublicScanReportResponse>(
    `${SCAN_ENDPOINT}/${reportToken}/report`
  );
  return response.data;
};
